import { ENDPOINTS } from 'config/endpoints';
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { TaskPatch } from 'crono-fe-common/types/DTO/taskPatch';
import { MutationMeta, withMeta } from 'crono-fe-common/types/mutationMeta';
import { useBackgroundInvalidateAutomatorDelay } from 'crono-fe-common/hooks/crono-extension/gateway';
import { invalidateTimelineQueries } from 'hooks/services/timeline/useSearchTimeline';
import { useAccountDrawer } from 'context/accountDrawer';
import { useConfirmModal } from 'context/confirmModal';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';

interface PatchTaskOptions {
  invalidateNextTask?: boolean;
}

export default function usePatchTask(options?: PatchTaskOptions) {
  const queryClient = useQueryClient();
  const { drawerContent, updateDrawerContent } = useAccountDrawer();
  const { isManager } = useRoleCapabilities();

  const patchTask = ({ onBehalf, ...taskPatch }: TaskPatch) => {
    const request = createPatchTaskRequest({
      ...taskPatch,
      //The flag is omitted rather than sent as false: non-managers sending it at all are rejected with 400
      ...(onBehalf && isManager && { onBehalf: true }),
    });
    return client(request);
  };
  const { call: invalidateAutomatorDelay } =
    useBackgroundInvalidateAutomatorDelay();
  return useMutation<Response<null>, CronoError, TaskPatch>(patchTask, {
    onSuccess: (_d, variables) => {
      // Update windowContent.task if it matches the patched task
      if (!drawerContent?.task || drawerContent.task.id !== variables.id) {
        return;
      }

      //onBehalf is a request flag, not a task field, so it must not reach the cached task
      const { id, onBehalf, ...patchedFields } = variables;
      if (Object.keys(patchedFields).length > 0) {
        const updatedTask = {
          ...drawerContent.task,
          ...patchedFields,
        };
        updateDrawerContent({ task: updatedTask });
      }
    },
    onSettled: (_d, _e, variables) => {
      if (
        variables.automatic ||
        variables.delay !== undefined ||
        variables.activityDate !== undefined
      ) {
        invalidateAutomatorDelay({});
      }
      invalidatePatchTaskQueries(queryClient, options);
    },
  });
}

export function createPatchTaskRequest(taskPatch: TaskPatch): Request {
  return {
    url: `${ENDPOINTS.task.main}`,
    config: {
      method: 'patch',
      data: { data: taskPatch },
    },
  };
}

export function invalidatePatchTaskQueries(
  queryClient: QueryClient,
  options?: PatchTaskOptions,
) {
  return Promise.all([
    queryClient.invalidateQueries([ENDPOINTS.task.main]),
    queryClient.invalidateQueries([ENDPOINTS.task.search]),
    queryClient.invalidateQueries([ENDPOINTS.task.overdue]),
    ...(options?.invalidateNextTask === false
      ? []
      : [queryClient.invalidateQueries([ENDPOINTS.task.next])]),
    queryClient.invalidateQueries([ENDPOINTS.task.aggregate]),
    queryClient.invalidateQueries([ENDPOINTS.task.total]),
    queryClient.invalidateQueries([ENDPOINTS.task.dailyQuantity]),
    queryClient.invalidateQueries([ENDPOINTS.task.automationErrors]),

    queryClient.invalidateQueries([ENDPOINTS.strategy.main]),
    queryClient.invalidateQueries([ENDPOINTS.strategy.prospect]),
    queryClient.invalidateQueries([ENDPOINTS.strategy.search]),
    queryClient.invalidateQueries([ENDPOINTS.strategy.details]),
    queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]),

    queryClient.invalidateQueries([ENDPOINTS.task.linkedin.quantity]),
    queryClient.invalidateQueries([ENDPOINTS.task.linkedin.automatic.check]),

    invalidateTimelineQueries(queryClient),
  ]);
}

export const useSkipTaskWithConfirm = (
  onSuccess?: () => void,
  meta?: MutationMeta,
  options?: PatchTaskOptions,
) => {
  const { openModal: openConfirmModal } = useConfirmModal();
  const { mutate: patchTask, ...rest } = usePatchTask(options);

  //onBehalf skips a task owned by another user, as its owner. Manager only
  const skipTaskWithConfirm = (id: number, onBehalf?: boolean) => {
    openConfirmModal({
      title: 'Are you sure you want to skip this task?',
      text: 'The task will be skipped',
      cancelText: 'No',
      confirmText: 'Yes',
      confirmFunction: () => {
        patchTask(withMeta({ id: id, skipped: true, onBehalf }, meta ?? {}), {
          onSuccess,
        });
      },
      cancelFunction: () => null,
    });
  };

  return { skipTaskWithConfirm, ...rest };
};
